import { Controller, useForm } from 'react-hook-form'
import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'

import { validationMessages } from 'constants/validation'
import {
	Stack, StackProps,	
	Rating,
	TextField,
	Autocomplete,
	Box
} from '@mui/material'

import { Section } from 'components/controls/section'

import { SearchsSenmoniFormData } from 'interface/search/senmoni'
import { useListSenmoni } from 'interface/senmoni'
import { Loading } from 'components/controls/loading'

const factorList = [
  { factor: 0.2, value: 1, description: '星1' },
  { factor: 0.5, value: 2, description: '星2' },
  { factor: 1.0, value: 3, description: '星3' },
  { factor: 2.0, value: 4, description: '星4' },
  { factor: 3.0, value: 5, description: '星5' },
]

const {
  Required
} = validationMessages

const formSchema = yup.object().shape({
  senmoniCode: yup.string().required(Required),
	ninteiiFactor: yup.number().required(Required),
	senmoniFactor: yup.number().required(Required),
	sidouiFactor: yup.number().required(Required)
})

type useSearchsSenmoniFormProps = {
	searchId: number
	data?: SearchsSenmoniFormData
	onSubmit: (data: SearchsSenmoniFormData) => void
}
export const useSearchsSenmoniForm = ({
	searchId,
	data,
	onSubmit
}: useSearchsSenmoniFormProps) => {
	const senmoniListState = useListSenmoni(searchId, null)
	const form = useForm({
		defaultValues: data ? {
			senmoniCode: data.senmoniCode,
			ninteiiFactor: data.ninteiiFactor ?? 0.5,
			senmoniFactor: data.senmoniFactor ?? 1,
			sidouiFactor: data.sidouiFactor ?? 2
		} : {
			senmoniCode: '',
			ninteiiFactor: 0.5,
			senmoniFactor: 1,
			sidouiFactor: 2
		},
		mode: 'onChange',
		resolver: yupResolver(formSchema)
	})
	return {
		...form,
		senmoniListState,
		submit: form.handleSubmit(input => onSubmit({
			senmoniCode: input.senmoniCode,
			ninteiiFactor: input.ninteiiFactor,
			senmoniFactor: input.senmoniFactor,
			sidouiFactor: input.sidouiFactor,
			updateTime: data?.updateTime
		}))
	}
}

export const SearchsSenmoniForm = ({
	control,
	setValue,
	formState: { errors },
	senmoniListState,
	...others
}: ReturnType<typeof useSearchsSenmoniForm> & StackProps) => {
	return (
		<>
      { senmoniListState.loading && <Loading /> }

			<Stack direction="column" {...others}>
				<Section label='専門医' labelWidth='6em' labelProps={{ paddingY: 2 }}>
					<Controller
						control={control}
						name="senmoniCode"
						render={({ field }) => (
							<Autocomplete
								ref={field.ref}
								value={senmoniListState.data?.list.find(
									(senmoni) => senmoni.senmoniCode === field.value
								) ?? null}
								size='small'
								renderInput={(params) => (
									<TextField
										{...params}
										error={!!errors.senmoniCode}
										helperText={errors.senmoniCode ? errors.senmoniCode.message : ''}
									/>
								)}
								getOptionLabel={senmoni => senmoni.senmoniName ?? ''}
								options={senmoniListState.data?.list ?? []}
								renderOption={(props, senmoni) => (
									<Box component="li" {...props}>
										{ senmoni.count ? `${senmoni.senmoniName}（${senmoni.count}人）` : senmoni.senmoniName }
									</Box>
								)}
                onChange={(_, value) => {
                  const senmoniCode = value?.senmoniCode ?? ''
                  setValue("senmoniCode", senmoniCode, { shouldValidate: true })
                }}
							/>
						)}
					/>
				</Section>

				<Section label="認定医" labelProps={{ paddingY: 2 }}>
					<Controller
						control={control}
						name="ninteiiFactor"
						render={({ field }) => (
							<Rating
								value={factorList.find(r => r.factor === field.value)?.value ?? 0}
								name="ninteiiFactor"
								onChange={(e, v) => field.onChange(e, factorList.find(r => r.value === v)?.factor ?? 10)}
							/>
						)}
					/>
				</Section>

				<Section label="専門医" labelProps={{ paddingY: 2 }}>
					<Controller
						control={control}
						name="senmoniFactor"
						render={({ field }) => (
							<Rating
								value={factorList.find(r => r.factor === field.value)?.value ?? 0}
								name="senmoniFactor"
								onChange={(e, v) => field.onChange(e, factorList.find(r => r.value === v)?.factor ?? 10)}
							/>
						)}
					/>
				</Section>

				<Section label="指導医" labelProps={{ paddingY: 2 }}>
					<Controller
						control={control}
						name="sidouiFactor"
						render={({ field }) => (
							<Rating
								value={factorList.find(r => r.factor === field.value)?.value ?? 0}
								name="sidouiFactor"
								onChange={(e, v) => field.onChange(e, factorList.find(r => r.value === v)?.factor ?? 10)}
							/>
						)}
					/>
				</Section>
			</Stack>
		</>
	)
}